// Main Cart Page JavaScript Functionality

const CartUtils = {
  async isSampleProduct(itemHandle) {
    try {
      const productResponse = await fetch(`/products/${itemHandle}.js`);
      const productData = await productResponse.json();
      return productData.tags?.includes('cart-sample');
    } catch {
      return false;
    }
  },

  async isExcludedProduct(itemHandle) {
    try {
      const productResponse = await fetch(`/products/${itemHandle}.js`);
      const productData = await productResponse.json();
      return productData.tags?.includes('gift-sample-excluded');
    } catch {
      return false;
    }
  },

  isFreeGift(item) {
    return item.properties?._free_gift === 'true' || item.properties?._free_gift === true;
  },

  isSample(item) {
    return item.product?.tags?.includes('cart-sample');
  },

  async categorizeCartItems(cartItems) {
    const regularProducts = [];
    const sampleProducts = [];
    const excludedProducts = [];
    for (const item of cartItems) {
      if (this.isFreeGift(item)) continue;
      const isSample = await this.isSampleProduct(item.handle);
      if (isSample) {
        sampleProducts.push(item);
      } else {
        const isExcluded = await this.isExcludedProduct(item.handle);
        if (isExcluded) {
          excludedProducts.push(item);
        } else {
          regularProducts.push(item);
        }
      }
    }
    return { regularProducts, sampleProducts, excludedProducts };
  },

  async hasEligibleProducts(cartItems) {
    const { regularProducts } = await this.categorizeCartItems(cartItems);
    return regularProducts.length > 0;
  },

  async getEligibleCartTotal(cartItems) {
    let eligibleTotal = 0;
    let hasEligibleProducts = false;
    
    // Only count eligible products - excluded products never count toward thresholds
    for (const item of cartItems) {
      if (this.isFreeGift(item)) continue;
      const isSample = await this.isSampleProduct(item.handle);
      if (isSample) continue;
      
      const isExcluded = await this.isExcludedProduct(item.handle);
      if (!isExcluded) {
        eligibleTotal += item.final_line_price;
        hasEligibleProducts = true;
      }
    }
    
    return { total: eligibleTotal, hasEligible: hasEligibleProducts };
  },

  async shouldShowExclusionMessage(cartItems) {
    const { excludedProducts } = await this.categorizeCartItems(cartItems);
    if (excludedProducts.length === 0) return false;
    
    const hasEligible = await this.hasEligibleProducts(cartItems);
    return !hasEligible;
  },

  async updateCartQuantities(updates) {
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ updates })
    });
  },

  async fixItemQuantity(lineKey, quantity = 1) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity })
    });
  },

  async removeCartItems(items) {
    return Promise.all(items.map(item => this.fixItemQuantity(item.key, 0)));
  },

  async getCart() {
    return (await fetch('/cart.js')).json();
  },

  async fixSampleAndGiftQuantities(cartItems) {
    const updates = {};
    cartItems.forEach(item => {
      if ((this.isFreeGift(item) || this.isSample(item)) && item.quantity !== 1) {
        updates[item.key] = 1;
      }
    });
    if (Object.keys(updates).length === 0) return false;
    await this.updateCartQuantities(updates);
    return true;
  }
};

class MainCart extends HTMLElement {
  constructor() {
    super();
    this.config = window.mainCartConfig || {};
  }

  connectedCallback() {
    if (this.config.samplesEnabled) {
      this.initSampleSectionManager();
      this.initSampleCleanupManager();
      this.initSampleCardHandler();
    }
    if (this.config.freeGiftEnabled && this.config.freeGiftConfig) {
      this.initFreeGiftManager();
    }
    this.initCartFormHandler();
    this.enforceQuantitiesOnLoad();
  }

  async enforceQuantitiesOnLoad() {
    try {
      const cartData = await CartUtils.getCart();
      if (await CartUtils.fixSampleAndGiftQuantities(cartData.items)) {
        window.location.reload();
        return;
      }
      this.updateDOMInputs(cartData.items);
    } catch (error) {
      console.error('Error enforcing quantities:', error);
    }
  }

  updateDOMInputs(cartItems) {
    const cartForm = this.querySelector('#cart-main-form');
    if (!cartForm) return;
    cartForm.querySelectorAll('input[name="updates[]"]').forEach(input => {
      const lineKey = input.getAttribute('data-line-key');
      const cartItem = cartItems.find(item => item.key === lineKey);
      if (cartItem && (CartUtils.isFreeGift(cartItem) || CartUtils.isSample(cartItem))) {
        input.setAttribute('readonly', 'readonly');
        input.setAttribute('max', '1');
        input.setAttribute('min', '1');
        input.value = 1;
      }
    });
  }

  async fixQuantitiesBeforeCheckout() {
    try {
      const cartData = await CartUtils.getCart();
      await CartUtils.fixSampleAndGiftQuantities(cartData.items);
      const verifyCart = await CartUtils.getCart();
      await CartUtils.fixSampleAndGiftQuantities(verifyCart.items);
    } catch (error) {
      console.error('Error fixing quantities:', error);
    }
  }

  async prepareCheckout(cartForm) {
    await this.fixQuantitiesBeforeCheckout();
    const cartData = await CartUtils.getCart();
    const updates = {};
    cartData.items.forEach(item => {
      if (CartUtils.isFreeGift(item) || CartUtils.isSample(item)) {
        updates[item.key] = 1;
      } else {
        const input = cartForm.querySelector(`input[data-line-key="${item.key}"]`);
        updates[item.key] = input ? parseInt(input.value) : item.quantity;
      }
    });
    await CartUtils.updateCartQuantities(updates);
    await this.fixQuantitiesBeforeCheckout();
    await new Promise(resolve => setTimeout(resolve, 200));
    window.location.replace('/checkout');
  }

  initCartFormHandler() {
    const cartForm = this.querySelector('#cart-main-form');
    if (!cartForm) return;

    const sampleAndGiftKeys = new Set();
    const updateKeysCache = async () => {
      try {
        const cartData = await CartUtils.getCart();
        sampleAndGiftKeys.clear();
        cartData.items.forEach(item => {
          if (CartUtils.isFreeGift(item) || CartUtils.isSample(item)) {
            sampleAndGiftKeys.add(item.key);
          }
        });
      } catch (error) {
        console.error('Error updating keys cache:', error);
      }
    };
    updateKeysCache();
    ['cart:change', 'cart:refresh'].forEach(event => {
      document.addEventListener(event, updateKeysCache);
    });

    const isSampleOrGiftInput = (input) => {
      if (!input?.hasAttribute('data-line-key')) return false;
      return input.hasAttribute('readonly') || sampleAndGiftKeys.has(input.getAttribute('data-line-key'));
    };

    ['input', 'change', 'keydown', 'keyup', 'paste', 'beforeinput'].forEach(eventType => {
      cartForm.addEventListener(eventType, (event) => {
        const input = event.target;
        if (input.name !== 'updates[]' || !isSampleOrGiftInput(input)) return;
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        input.value = 1;
        if (event.type === 'keydown' || event.type === 'keyup') input.blur();
        return false;
      }, true);
    });

    cartForm.addEventListener('click', async (event) => {
      const target = event.target.closest('button[name="checkout"], input[name="checkout"]');
      if (target?.name === 'checkout') {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        target.setAttribute('aria-busy', 'true');
        await this.prepareCheckout(cartForm);
      }
    }, true);

    cartForm.addEventListener('submit', async (event) => {
      if (event.submitter?.name === 'checkout') {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        event.submitter.setAttribute('aria-busy', 'true');
        await this.prepareCheckout(cartForm);
        return false;
      }

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      try {
        const cartData = await CartUtils.getCart();
        const updates = {};
        cartForm.querySelectorAll('input[name="updates[]"]').forEach(input => {
          const lineKey = input.getAttribute('data-line-key');
          if (!lineKey) return;
          const cartItem = cartData.items.find(item => item.key === lineKey);
          if (cartItem && (CartUtils.isFreeGift(cartItem) || CartUtils.isSample(cartItem))) {
            updates[lineKey] = 1;
          } else {
            const quantity = parseInt(input.value);
            if (!isNaN(quantity) && quantity >= 0) updates[lineKey] = quantity;
          }
        });
        await CartUtils.updateCartQuantities(updates);
        window.location.reload();
      } catch (error) {
        console.error('Error updating cart:', error);
        window.location.reload();
      }
      return false;
    }, true);
  }

  initSampleSectionManager() {
    let isChecking = false;
    const checkCart = async () => {
      if (isChecking) return;
      isChecking = true;
      try {
        const samplesSection = this.querySelector('.cart-samples-section');
        if (!samplesSection) return;
        const cartData = await CartUtils.getCart();
        const { regularProducts, sampleProducts } = await CartUtils.categorizeCartItems(cartData.items);
        if (regularProducts.length === 0 && sampleProducts.length > 0) {
          await CartUtils.removeCartItems(sampleProducts);
          window.location.reload();
          return;
        }
        // Hide samples section if there are samples OR if there are no eligible products
        const hasEligible = await CartUtils.hasEligibleProducts(cartData.items);
        samplesSection.classList.toggle('display-hidden', sampleProducts.length > 0 || !hasEligible);
      } catch (error) {
        console.error('Error checking cart:', error);
      } finally {
        isChecking = false;
      }
    };
    checkCart();
    ['cart:refresh'].forEach(event => {
      document.addEventListener(event, checkCart);
      document.documentElement.addEventListener(event, checkCart);
    });
    document.addEventListener('change', (e) => {
      if (e.target.matches('input[name="updates[]"]')) setTimeout(checkCart, 500);
    });
    document.addEventListener('click', (e) => {
      if (e.target.closest('a[href*="url_to_remove"]')) setTimeout(checkCart, 500);
    });
  }

  initFreeGiftManager() {
    if (!window.cartPageFreeGiftManager && (this.config.freeGiftConfig || this.config.freeGiftConfig2)) {
      this.freeGiftManager = new CartPageFreeGiftManager({
        gift1: this.config.freeGiftConfig,
        gift2: this.config.freeGiftConfig2
      });
      window.cartPageFreeGiftManager = this.freeGiftManager;
    }
  }

  initSampleCleanupManager() {
    if (!window.cartPageSampleCleanupManager) {
      this.sampleCleanupManager = new CartPageSampleCleanupManager();
      window.cartPageSampleCleanupManager = this.sampleCleanupManager;
    }
  }

  initSampleCardHandler() {
    if (window.cartPageSampleCardHandlerInitialized) return;
    window.cartPageSampleCardHandlerInitialized = true;
    
    document.addEventListener('click', async (event) => {
      const card = event.target.closest('.sample-product-card');
      if (!card || card.classList.contains('sample-product-card--adding')) return;
      const productId = card.dataset.productId;
      if (!productId) return;
      const isOnCartPage = document.querySelector('.shopify-section--main-cart') !== null;
      if (!isOnCartPage) return;

      const samplesSection = this.querySelector('.cart-samples-section');
      if (samplesSection) samplesSection.classList.add('display-hidden');
      card.classList.add('sample-product-card--adding');

      try {
        const cartData = await CartUtils.getCart();
        const removePromises = [];
        for (const item of cartData.items) {
          if (!CartUtils.isFreeGift(item)) {
            const isSample = await CartUtils.isSampleProduct(item.handle);
            if (isSample) removePromises.push(CartUtils.fixItemQuantity(item.key, 0));
          }
        }
        await Promise.all(removePromises);
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: productId, quantity: 1 })
        });
        window.location.reload();
      } catch (error) {
        console.error('Error adding sample:', error);
        window.location.reload();
      }
    });
  }
}

class CartPageFreeGiftManager {
  constructor(config) {
    this.gift1Config = config.gift1 || null;
    this.gift2Config = config.gift2 || null;
    this.isProcessing = false;
    this.init();
  }
  
  init() {
    document.addEventListener('cart:change', async (e) => await this.checkAndManageFreeGifts(e.detail.cart));
    ['cart:refresh', 'DOMContentLoaded'].forEach(event => {
      document.addEventListener(event, () => this.checkInitialCart());
      if (event === 'cart:refresh') document.documentElement.addEventListener(event, () => this.checkInitialCart());
    });
    document.addEventListener('change', (e) => {
      if (e.target.matches('input[name="updates[]"]')) setTimeout(() => this.checkInitialCart(), 500);
    });
  }
  
  async checkInitialCart() {
    try {
      const cart = await CartUtils.getCart();
      await this.checkAndManageFreeGifts(cart);
    } catch (error) {
      console.error('Error checking cart:', error);
    }
  }
  
  async checkAndManageFreeGifts(cart) {
    if (this.isProcessing) return;
    
    let hasGift1 = false, hasGift2 = false;
    let gift1Item = null, gift2Item = null;
    
    // Track all free gifts to identify orphaned ones (not matching current config)
    const orphanedFreeGifts = [];
    
    cart.items.forEach(item => {
      if (CartUtils.isFreeGift(item)) {
        const matchesGift1 = this.gift1Config && item.handle === this.gift1Config.freeGiftHandle;
        const matchesGift2 = this.gift2Config && item.handle === this.gift2Config.freeGiftHandle;
        
        if (matchesGift1) {
          hasGift1 = true;
          gift1Item = item;
        } else if (matchesGift2) {
          hasGift2 = true;
          gift2Item = item;
        } else {
          // This is a free gift that doesn't match current configuration - mark for removal
          orphanedFreeGifts.push(item);
        }
      }
    });
    
    // Remove any free gifts that don't match the current configuration
    if (orphanedFreeGifts.length > 0) {
      // Remove the first orphaned gift - this will trigger a cart refresh
      // and the function will be called again to remove the next one
      this.removeFreeGift(cart, orphanedFreeGifts[0].handle);
      return; // Exit early, will be called again after cart updates
    }

    // Remove duplicate GWP products added without _free_gift property
    const giftHandles = [
      this.gift1Config?.freeGiftHandle,
      this.gift2Config?.freeGiftHandle
    ].filter(Boolean);

    const duplicateGifts = cart.items.filter(item => {
      const isFreeGift = item.properties && item.properties._free_gift;
      return !isFreeGift && giftHandles.includes(item.handle);
    });

    if (duplicateGifts.length > 0) {
      await CartUtils.removeCartItems(duplicateGifts);
      window.location.reload();
      return;
    }

    if (hasGift1 && gift1Item?.quantity !== 1) {
      this.fixFreeGiftQuantity(gift1Item.key);
      return;
    }
    if (hasGift2 && gift2Item?.quantity !== 1) {
      this.fixFreeGiftQuantity(gift2Item.key);
      return;
    }
    
    // Check if we have eligible products (excluding excluded products if no eligible products exist)
    const { total: eligibleTotal, hasEligible } = await CartUtils.getEligibleCartTotal(cart.items);
    
    // If there are no eligible products, remove all free gifts
    if (!hasEligible) {
      if (hasGift1) this.removeFreeGift(cart, this.gift1Config.freeGiftHandle);
      if (hasGift2) this.removeFreeGift(cart, this.gift2Config.freeGiftHandle);
      return;
    }
    
    // Calculate cart total using eligible total (which includes excluded products if eligible products exist)
    let cartTotal = eligibleTotal;
    
    if (cart.cart_level_discount_applications) {
      cart.cart_level_discount_applications.forEach(discount => {
        cartTotal -= discount.total_allocated_amount;
      });
    }
    
    const threshold1 = this.gift1Config?.threshold ?? Infinity;
    const threshold2 = this.gift2Config?.threshold ?? Infinity;
    const shouldHaveGift2 = cartTotal >= threshold2 && this.gift2Config;
    const shouldHaveGift1 = !shouldHaveGift2 && cartTotal >= threshold1 && this.gift1Config;
    
    if (shouldHaveGift2) {
      if (hasGift1 && !hasGift2) {
        this.removeFreeGift(cart, this.gift1Config.freeGiftHandle, () => this.addFreeGift(this.gift2Config));
      } else if (hasGift1 && hasGift2) {
        this.removeFreeGift(cart, this.gift1Config.freeGiftHandle);
      } else if (!hasGift1 && !hasGift2) {
        this.addFreeGift(this.gift2Config);
      }
    } else if (shouldHaveGift1) {
      if (hasGift2 && !hasGift1) {
        this.removeFreeGift(cart, this.gift2Config.freeGiftHandle, () => this.addFreeGift(this.gift1Config));
      } else if (hasGift1 && hasGift2) {
        this.removeFreeGift(cart, this.gift2Config.freeGiftHandle);
      } else if (!hasGift1 && !hasGift2) {
        this.addFreeGift(this.gift1Config);
      }
    } else {
      if (hasGift1) this.removeFreeGift(cart, this.gift1Config.freeGiftHandle);
      if (hasGift2) this.removeFreeGift(cart, this.gift2Config.freeGiftHandle);
    }
  }
  
  async addFreeGift(giftConfig) {
    if (this.isProcessing || !giftConfig) return;
    this.isProcessing = true;
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          id: giftConfig.freeGiftProductId,
          quantity: 1,
          properties: { '_free_gift': 'true' }
        })
      });
      if (response.ok) window.location.reload();
    } catch (error) {
      console.error('Error adding free gift:', error);
    } finally {
      this.isProcessing = false;
    }
  }
  
  async removeFreeGift(cart, giftHandle, callback) {
    if (this.isProcessing) return;
    this.isProcessing = true;
    try {
      const freeGiftItem = cart.items.find(item => item.handle === giftHandle && item.properties && item.properties._free_gift);
      if (freeGiftItem) {
        await CartUtils.fixItemQuantity(freeGiftItem.key, 0);
        callback ? setTimeout(callback, 300) : window.location.reload();
      }
    } catch (error) {
      console.error('Error removing free gift:', error);
    } finally {
      this.isProcessing = false;
    }
  }
  
  async fixFreeGiftQuantity(lineKey) {
    if (this.isProcessing) return;
    this.isProcessing = true;
    try {
      await CartUtils.fixItemQuantity(lineKey, 1);
      window.location.reload();
    } catch (error) {
      console.error('Error fixing free gift quantity:', error);
    } finally {
      this.isProcessing = false;
    }
  }
}

class CartPageSampleCleanupManager {
  constructor() {
    this.isProcessing = false;
    this.init();
  }
  
  init() {
    document.addEventListener('cart:change', (e) => this.checkAndCleanupSamples(e.detail.cart));
    ['cart:refresh', 'DOMContentLoaded'].forEach(event => {
      document.addEventListener(event, () => this.checkInitialCart());
      if (event === 'cart:refresh') document.documentElement.addEventListener(event, () => this.checkInitialCart());
    });
    document.addEventListener('change', (e) => {
      if (e.target.matches('input[name="updates[]"]')) setTimeout(() => this.checkInitialCart(), 500);
    });
  }
  
  async checkInitialCart() {
    try {
      const cart = await CartUtils.getCart();
      this.checkAndCleanupSamples(cart);
    } catch (error) {
      console.error('Error checking cart:', error);
    }
  }
  
  async checkAndCleanupSamples(cart) {
    if (this.isProcessing) return;
    const { regularProducts, sampleProducts } = await CartUtils.categorizeCartItems(cart.items);
    
    for (const sample of sampleProducts) {
      if (sample.quantity !== 1) {
        this.fixSampleQuantity(sample.key);
        return;
      }
    }
    
    if (regularProducts.length === 0 && sampleProducts.length > 0) {
      this.removeSamples(sampleProducts);
      return;
    }
    
    if (sampleProducts.length > 1) {
      this.removeSamples(sampleProducts.slice(0, -1));
    }
  }
  
  async removeSamples(sampleProducts) {
    if (this.isProcessing) return;
    this.isProcessing = true;
    try {
      await CartUtils.removeCartItems(sampleProducts);
      window.location.reload();
    } catch (error) {
      console.error('Error removing samples:', error);
    } finally {
      this.isProcessing = false;
    }
  }
  
  async fixSampleQuantity(lineKey) {
    if (this.isProcessing) return;
    this.isProcessing = true;
    try {
      await CartUtils.fixItemQuantity(lineKey, 1);
      window.location.reload();
    } catch (error) {
      console.error('Error fixing sample quantity:', error);
    } finally {
      this.isProcessing = false;
    }
  }
}

customElements.define('main-cart', MainCart);
